import { apiRequest } from '../../services/apiClient';
import { UserProfile } from './userSession';
import { joinPhone, LoginSchema, SignupSchema, SocialSignupSchema } from './authSchema';

export interface AuthResult {
  token: string;
  user: UserProfile;
}

export function signupUser(values: SignupSchema): Promise<AuthResult> {
  return apiRequest<AuthResult>('/api/users/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      phone: joinPhone(values),
      password: values.password,
      name: values.name,
      email: values.email,
    }),
  });
}

export function loginUser(values: LoginSchema): Promise<AuthResult> {
  return apiRequest<AuthResult>('/api/users/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: values.email, password: values.password }),
  });
}

// 처음 소셜 인증한 사용자는 바로 가입되지 않고, 가입 화면에서 이름/휴대폰번호를 입력해야 한다
export interface SocialSignupRequired {
  signupRequired: true;
  signupToken: string;
  provider: 'kakao' | 'naver';
  email: string | null;
}

export type SocialAuthResult = AuthResult | SocialSignupRequired;

export function loginWithKakao(code: string, redirectUri: string): Promise<SocialAuthResult> {
  return apiRequest<SocialAuthResult>('/api/users/oauth/kakao', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirectUri }),
  });
}

export function loginWithNaver(code: string, state: string): Promise<SocialAuthResult> {
  return apiRequest<SocialAuthResult>('/api/users/oauth/naver', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, state }),
  });
}

export function signupWithSocial(
  signupToken: string,
  values: SocialSignupSchema,
): Promise<AuthResult> {
  return apiRequest<AuthResult>('/api/users/oauth/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      signupToken,
      phone: joinPhone(values),
      name: values.name,
      email: values.email,
    }),
  });
}

export function checkEmailAvailable(email: string): Promise<{ available: boolean }> {
  return apiRequest<{ available: boolean }>(`/api/users/email/check?email=${encodeURIComponent(email)}`);
}

export function sendEmailCode(email: string): Promise<{ expiresInSeconds: number }> {
  return apiRequest<{ expiresInSeconds: number }>('/api/users/email/send-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export function verifyEmailCode(email: string, code: string): Promise<{ verified: boolean }> {
  return apiRequest<{ verified: boolean }>('/api/users/email/verify-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
}

// 간편가입 휴대폰 인증. 발송은 소셜 인증 후 받은 가입용 토큰이 있어야 한다
export function sendPhoneCode(
  signupToken: string,
  phone: string,
  name: string,
): Promise<{ expiresInSeconds: number }> {
  return apiRequest<{ expiresInSeconds: number }>('/api/users/phone/send-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ signupToken, phone, name }),
  });
}

export function verifyPhoneCode(phone: string, code: string): Promise<{ verified: boolean }> {
  return apiRequest<{ verified: boolean }>('/api/users/phone/verify-code', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone, code }),
  });
}
